/**
 * Global company scope (multi-company app-bar scope — WI-1).
 *
 * A SINGLE per-user company filter across the whole app, surfaced by the
 * app-bar `CompanyScopeSwitcher`. This is the new global affordance that
 * supersedes the older per-module sticky picker (`ActiveCompanyContext`) for
 * participating (ERP) modules — see
 * `docs/platform/multi-company-design.md` §"Global company scope (app bar)".
 *
 * `null` = "All companies" (the default). The value is sent to the backend via
 * the `X-Active-Company-Id` header for participating modules — a UI list-filter
 * hint, NOT a security boundary (RLS / accessible_company_ids remain the
 * boundary; see shared/dependencies/auth.py).
 *
 * Persistence: one localStorage key `panluma_company_scope`, so the selection
 * survives reloads. The scope is intentionally global (not per-module) — the
 * design treats company as one scope across the app, not N per-module filters.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AUTH_STORAGE_KEYS } from '../auth/storageKeys';

const STORAGE_KEY = 'panluma_company_scope';

function readScope(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

function writeScope(companyId: string | null): void {
  try {
    if (companyId) {
      localStorage.setItem(STORAGE_KEY, companyId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable (private mode / SSR) — scope stays in-memory.
  }
}

/**
 * The caller's accessible companies, read from the JWT's signed claim.
 *
 * `null` means "no claim on this token" — either an unauthenticated/legacy token
 * or a caller the backend resolved to an empty accessible set (the claim is
 * omitted when empty). Both are treated the same by `getCompanyScopeValue`
 * below: unverifiable, therefore don't send the scope.
 */
function readAccessibleCompanyIds(): string[] | null {
  try {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const claims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const ids = claims?.accessible_company_ids;
    return Array.isArray(ids) && ids.length > 0 ? (ids as string[]) : null;
  } catch {
    return null;
  }
}

/**
 * Whether `companyId` is one the JWT's signed claim says the caller can access.
 *
 * The backend's governed read routes fail closed on a company the caller cannot
 * access (`resolve_active_company_scope` → 403), and an errored React Query is
 * not refetched when `CompanyScopeSwitcher` later self-heals — so a stale
 * persisted company id strands the user on "Failed to load". Every id bound for
 * the `X-Active-Company-Id` header passes through here first: apply the same
 * rule the backend does, against the same signed source, one step earlier.
 * Never send a header the backend is going to reject.
 *
 * Omitting the header is always safe — it means "All companies", which every
 * route allows. This is a UX guard, NOT a security boundary: the header remains
 * client-controlled and the backend resolver stays the real gate.
 */
export function isCompanyAccessible(companyId: string): boolean {
  const accessible = readAccessibleCompanyIds();
  return accessible !== null && accessible.includes(companyId);
}

/**
 * Read the global company scope without React (used by the axios interceptor
 * to populate `X-Active-Company-Id` for participating modules). Returns only a
 * scope the caller can actually access — see {@link isCompanyAccessible}.
 */
export function getCompanyScopeValue(): string | null {
  const scope = readScope();
  if (!scope) return null;
  return isCompanyAccessible(scope) ? scope : null;
}

interface CompanyScopeContextType {
  /** Current global company scope (null = "All companies"). */
  companyScopeId: string | null;
  /** Set the global company scope (null clears it back to "All companies"). */
  setCompanyScope: (companyId: string | null) => void;
}

const CompanyScopeContext = createContext<CompanyScopeContextType | undefined>(undefined);

export function CompanyScopeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [companyScopeId, setCompanyScopeId] = useState<string | null>(() => readScope());

  // Persist to localStorage whenever the scope changes.
  useEffect(() => {
    writeScope(companyScopeId);
  }, [companyScopeId]);

  const setCompanyScope = useCallback((companyId: string | null) => {
    setCompanyScopeId((prev) => (prev === companyId ? prev : companyId));
  }, []);

  // Invalidate cached queries when the scope actually changes so list/report
  // surfaces refetch with the new X-Active-Company-Id header. Skip the very
  // first run (initial value) to avoid an unnecessary eviction on mount.
  //
  // Scope to ACTIVE queries only. A blanket invalidateQueries() walks the whole
  // 24h-gcTime cache and re-renders every mounted observer in one synchronous
  // commit — on a busy session that froze the app for 2-4s when switching
  // company. Because the QueryClient runs with staleTime:0 (see index.tsx),
  // every inactive/unmounted query already refetches the moment it next mounts,
  // so it needs no eviction here — only the currently-mounted views must be
  // refreshed in place. (startTransition can't help: React Query subscribes via
  // useSyncExternalStore, whose updates React always renders synchronously.)
  const lastScopeRef = useRef<string | null>(companyScopeId);
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      lastScopeRef.current = companyScopeId;
      return;
    }
    if (lastScopeRef.current === companyScopeId) return;
    lastScopeRef.current = companyScopeId;
    queryClient.invalidateQueries({ type: 'active' });
  }, [companyScopeId, queryClient]);

  const value = useMemo(
    () => ({ companyScopeId, setCompanyScope }),
    [companyScopeId, setCompanyScope],
  );

  return (
    <CompanyScopeContext.Provider value={value}>
      {children}
    </CompanyScopeContext.Provider>
  );
}

/** Access the global company scope (current value + setter). */
export function useCompanyScope(): CompanyScopeContextType {
  const ctx = useContext(CompanyScopeContext);
  if (!ctx) {
    throw new Error('useCompanyScope must be used within a CompanyScopeProvider');
  }
  return ctx;
}
